import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, Save, Lock, Unlock, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import ImageUploader from "./ImageUploader";
import SportConfigsForm from "./SportConfigsForm";
import FacilityPicker from "./FacilityPicker";
import { api, formatApiError } from "../lib/api";
import { COUNTRY_LIST } from "../lib/countries";

const EMPTY = {
  name: "", description: "",
  country: "", city: "", address: "",
  phone: "", email: "", website: "",
  logo_url: "", cover_url: "", sport_ids: [], sport_configs: [],
  is_private: false,
};

/**
 * Shared dialog for creating OR editing a club.
 *
 * Modes:
 *   - create: trigger renders the "+ Create club" button; on save POSTs `/api/clubs`
 *   - edit:   trigger is `triggerSlot` you pass in (typically a small "Edit"
 *             button on ClubDetail); on save PATCHes `/api/clubs/{id}`
 *
 * Props:
 *   - mode: "create" | "edit"
 *   - club: the existing doc when mode==="edit" (for prefill)
 *   - triggerSlot?: optional ReactNode used as DialogTrigger; falls back to a
 *                   default "+ Create club" pill button.
 *   - onSaved(club): callback with the fresh club doc after save
 */
export default function ClubFormDialog({ mode = "create", club = null, triggerSlot, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sports, setSports] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  // Selected home venue (from FacilityPicker) — used to pre-fill
  // city/country/address and auto-attach to the new club on save.
  const [pickedFacility, setPickedFacility] = useState(null);

  // Hydrate the form when the dialog opens
  useEffect(() => {
    if (!open) return;
    api.get("/sports").then((r) => setSports(r.data)).catch(() => setSports([]));
    if (mode === "edit" && club) {
      setForm({
        name: club.name || "",
        description: club.description || "",
        country: club.country || "",
        city: club.city || "",
        address: club.address || "",
        phone: club.phone || "",
        email: club.email || "",
        website: club.website || "",
        logo_url: club.logo_url || "",
        cover_url: club.cover_url || "",
        sport_ids: club.sport_ids || [],
        sport_configs: club.sport_configs || [],
        is_private: !!club.is_private,
      });
    } else {
      setForm(EMPTY);
    }
    setPickedFacility(null);
    setErrors({});
  }, [open, mode, club]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSport = (sid) => {
    setForm((f) => {
      const has = f.sport_ids.includes(sid);
      return { ...f, sport_ids: has ? f.sport_ids.filter((x) => x !== sid) : [...f.sport_ids, sid] };
    });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Required";
    if (form.email && !/.+@.+\..+/.test(form.email)) errs.email = "Invalid email";
    if (form.website && !/^https?:\/\//i.test(form.website)) errs.website = "Must start with http(s)://";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (!validate()) return;
    setBusy(true);
    try {
      const payload = { ...form, country: form.country ? form.country.toUpperCase() : null };
      const res = mode === "edit"
        ? await api.patch(`/clubs/${club.id}`, payload)
        : await api.post("/clubs", payload);
      // If a home venue was picked at creation time, attach it to the freshly
      // created club. Best-effort — the club save itself already succeeded,
      // so a link failure shouldn't block the success toast.
      if (mode === "create" && pickedFacility?.id && res.data?.id) {
        try {
          await api.post(`/clubs/${res.data.id}/facilities/${pickedFacility.id}`);
        } catch (attachErr) {
          // eslint-disable-next-line no-console
          console.warn("[club-create] facility attach failed:", attachErr?.response?.data?.detail || attachErr?.message);
          toast.warning("Club saved, but linking the venue failed. You can attach it later from the dashboard.");
        }
      }
      toast.success(mode === "edit" ? "Club updated" : "Club created");
      onSaved?.(res.data);
      setOpen(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message || "Save failed");
    } finally { setBusy(false); }
  };

  const defaultTrigger = (
    <button data-testid="club-create-btn" className="pp-btn-primary px-4 py-2 rounded-lg text-sm flex items-center gap-1.5">
      <Building2 size={14} /> Create club
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{triggerSlot || defaultTrigger}</DialogTrigger>
      <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-xl max-h-[90vh] overflow-y-auto" data-testid="club-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl flex items-center gap-2">
            <Building2 size={20} className="text-[#00E676]" />
            {mode === "edit" ? "Edit club" : "Create a new club"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <Field label="Club name *" error={errors.name}>
            <input data-testid="club-form-name" value={form.name} onChange={(e) => set("name", e.target.value)}
              className="pp-input w-full" placeholder="e.g. Riverside Padel Club" />
          </Field>

          <Field label="Description">
            <textarea data-testid="club-form-description" value={form.description} onChange={(e) => set("description", e.target.value)}
              rows={3} className="pp-input w-full resize-y" placeholder="Tell players what makes your club special" />
          </Field>

          {mode === "create" && (
            <Field label="Home venue (optional)">
              <FacilityPicker
                sportSlug={
                  sports.find((s) => form.sport_ids.includes(s.id))?.slug
                  || undefined
                }
                value={pickedFacility ? { facility_id: pickedFacility.id, location: [pickedFacility.name, pickedFacility.city, pickedFacility.country].filter(Boolean).join(", ") } : null}
                onChange={(v) => {
                  if (v?.facility_id) {
                    // Need the full facility doc to pre-fill address/city/country — fetch it by id
                    api.get(`/facilities/${v.facility_id}`).then((r) => {
                      const f = r.data;
                      setPickedFacility(f);
                      setForm((cur) => ({
                        ...cur,
                        city: cur.city || f.city || "",
                        country: cur.country || f.country || "",
                        address: cur.address || f.address || "",
                      }));
                    }).catch(() => setPickedFacility({ id: v.facility_id, name: v.location }));
                  } else {
                    setPickedFacility(null);
                  }
                }}
                testIdPrefix="club-facility"
              />
              {pickedFacility && (
                <div className="text-[11px] text-[#00E676] mt-1 flex items-center gap-1" data-testid="club-facility-linked">
                  <MapPin size={10} /> Home venue pre-selected — city/country/address pre-filled. You can still edit them below.
                </div>
              )}
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Country">
              <select data-testid="club-form-country" value={form.country} onChange={(e) => set("country", e.target.value)} className="pp-input w-full">
                <option value="">— Select —</option>
                {COUNTRY_LIST.map((c) => <option key={c.iso} value={c.iso}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="City">
              <input data-testid="club-form-city" value={form.city} onChange={(e) => set("city", e.target.value)}
                className="pp-input w-full" placeholder="e.g. Valencia" />
            </Field>
          </div>

          <Field label="Address">
            <input data-testid="club-form-address" value={form.address} onChange={(e) => set("address", e.target.value)}
              className="pp-input w-full" placeholder="Street address" />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Phone">
              <input data-testid="club-form-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)}
                className="pp-input w-full" placeholder="+34 600 000 000" />
            </Field>
            <Field label="Email" error={errors.email}>
              <input data-testid="club-form-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                className="pp-input w-full" placeholder="contact@club.com" />
            </Field>
          </div>

          <Field label="Website" error={errors.website}>
            <input data-testid="club-form-website" value={form.website} onChange={(e) => set("website", e.target.value)}
              className="pp-input w-full" placeholder="https://yourclub.com" />
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Logo">
              <ImageUploader
                value={form.logo_url}
                onUploaded={(url) => set("logo_url", url || "")}
                aspect="square"
                testId="club-form-logo"
                label="logo"
              />
            </Field>
            <Field label="Cover image">
              <ImageUploader
                value={form.cover_url}
                onUploaded={(url) => set("cover_url", url || "")}
                aspect="wide"
                testId="club-form-cover"
                label="cover"
              />
            </Field>
          </div>

          <Field label="Privacy">
            <button
              type="button"
              onClick={() => set("is_private", !form.is_private)}
              data-testid="club-form-privacy-toggle"
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                form.is_private
                  ? "border-[#FFD60A]/40 bg-[#FFD60A]/10 text-[#FFD60A]"
                  : "border-white/10 text-zinc-400 hover:border-white/30"
              }`}
            >
              {form.is_private ? <Lock size={14} /> : <Unlock size={14} />}
              {form.is_private ? "Private — admins approve join requests" : "Public — anyone can join"}
            </button>
          </Field>

          {sports.length > 0 && (
            <Field label="Sports offered">
              <div className="flex flex-wrap gap-1.5" data-testid="club-form-sports">
                {sports.map((s) => {
                  const picked = form.sport_ids.includes(s.id);
                  return (
                    <button
                      key={s.id} type="button"
                      data-testid={`club-form-sport-${s.slug}`}
                      onClick={() => toggleSport(s.id)}
                      className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                        picked ? "border-[#00E676] bg-[#00E676]/10 text-[#00E676]" : "border-white/10 text-zinc-400 hover:border-white/30"
                      }`}
                    >
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          {/* Per-sport configuration blocks — one card per selected sport */}
          {sports.length > 0 && form.sport_ids.length > 0 && (
            <Field label="Per-sport details">
              <SportConfigsForm
                selectedSports={sports.filter((s) => form.sport_ids.includes(s.id))}
                configs={form.sport_configs}
                onChange={(cfgs) => set("sport_configs", cfgs)}
              />
            </Field>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-white/5">
          <button onClick={() => setOpen(false)} className="pp-chip text-xs px-3 py-2">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="club-form-submit"
            className="pp-btn-primary px-5 py-2 rounded-lg text-sm flex items-center gap-1.5 disabled:opacity-50">
            <Save size={13} /> {busy ? "Saving…" : (mode === "edit" ? "Save changes" : "Create club")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-wider text-zinc-500 block mb-1">{label}</label>
      {children}
      {error && <div className="text-[11px] text-[#FF3B30] mt-1">{error}</div>}
    </div>
  );
}
